// React
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { addMonths } from "helpers";

// Servisni sloj
import {
  previewPricingOverrideAPI,
  savePricingOverrideAPI,
} from "services/api/pricing/overrideApi";
import {
  PricingOverrideEditFormValues,
  PricingOverridePreview,
} from "services/api/pricing/overrideTypes";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";

// UI
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import WizardStepBar from "ui/components/Wizard/WizardStepBar";

// Lokalne komponente
import OverrideAmountStep from "./components/OverrideAmountStep/OverrideAmountStep";
import OverrideSummaryCard from "./components/OverrideSummaryCard/OverrideSummaryCard";
import OverrideTargetStep from "./components/OverrideTargetStep/OverrideTargetStep";
import OverrideTermsStep from "./components/OverrideTermsStep/OverrideTermsStep";

// Tipovi i konstante
import { DEFAULT_MONTHS, OVERRIDE_MANDATORY_STEP_COUNT, OVERRIDE_STEP_LABELS } from "./consts";
import { PricingOverrideEditCardProps } from "./types";
import {
  OVERRIDE_AMOUNT_STEP_SCHEMA,
  OVERRIDE_TARGET_STEP_SCHEMA,
  OVERRIDE_TERMS_STEP_SCHEMA,
} from "./validation";

/** Shema koja se provjerava prije prelaska s koraka. */
const STEP_SCHEMAS = [
  OVERRIDE_TARGET_STEP_SCHEMA,
  OVERRIDE_AMOUNT_STEP_SCHEMA,
  OVERRIDE_TERMS_STEP_SCHEMA,
];

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

/**
 * Obrazac pregazivanja, korak po korak.
 *
 * Živo razlaganje s poslužitelja stoji na plohi lijevo: nitko ne množi grupu i pregazivanje
 * napamet, nego vidi što cijena postaje. Ishod „nije dopušteno" zaključava spremanje.
 */
const PricingOverrideEditCard: React.FC<PricingOverrideEditCardProps> = (
  props: PricingOverrideEditCardProps
) => {
  const { show, override, tenants, keyOptions, onSuccess, onClose } = props;

  const initialFormValues = useMemo<PricingOverrideEditFormValues>(
    () => ({
      id: override?.id ?? null,
      tenantId: override?.tenantId ?? null,
      keyCode: override?.keyCode ?? null,
      adjustmentType: override?.adjustmentType ?? "Percent",
      value: override?.value ?? null,
      reason: override?.reason ?? null,
      validFrom: override?.validFrom ?? toIsoDate(new Date()),
      validTo: override?.validTo ?? toIsoDate(addMonths(new Date(), DEFAULT_MONTHS)),
    }),
    [override]
  );

  const [formValues, setFormValues] = useState<PricingOverrideEditFormValues>(initialFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [preview, setPreview] = useState<PricingOverridePreview | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState<number>(0);

  // Dvoklik na „Nastavi" inače preskoči korak.
  const isProcessingRef = useRef<boolean>(false);

  // Modal ostaje montiran, pa se svako otvaranje mora vratiti na zapis s poslužitelja.
  useEffect(() => {
    if (!show) return;

    setFormValues(initialFormValues);
    setErrors({});
    setServerErrors([]);
    setPreview(null);
    setStepCurrent(0);
  }, [show, initialFormValues]);

  // Razlaganje se osvježava dok predstavnik tipka; odgoda štedi zahtjeve na svaku znamenku.
  useEffect(() => {
    const { tenantId, keyCode, adjustmentType, value } = formValues;

    if (!show || !tenantId || !keyCode || value === null) {
      setPreview(null);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const result = await previewPricingOverrideAPI(tenantId, keyCode, adjustmentType, value);

        if (!cancelled) setPreview(result);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formValues, show]);

  const tenantName = useMemo(() => {
    const tenant = tenants.find((item) => item.id === formValues.tenantId);

    return tenant ? tenant.name ?? tenant.companyName ?? tenant.id : null;
  }, [tenants, formValues.tenantId]);

  const keyOption = useMemo(
    () => keyOptions.find((item) => item.code === formValues.keyCode),
    [keyOptions, formValues.keyCode]
  );

  const isForbidden = preview?.outcome === "Forbidden";

  /*----------------------------------------------------------------
      NAVIGACIJA
  ----------------------------------------------------------------*/

  const handleNextStep = useCallback(() => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;

    try {
      if (!validateYup(formValues as any, STEP_SCHEMAS[stepCurrent], setErrors)) {
        return;
      }

      setErrors({});
      setStepCurrent((step) => step + 1);
    } finally {
      isProcessingRef.current = false;
    }
  }, [formValues, stepCurrent]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const handleStepClick = useCallback((stepIndex: number) => {
    setStepCurrent(stepIndex);
  }, []);

  /*----------------------------------------------------------------
      SPREMANJE
  ----------------------------------------------------------------*/

  const handleFinish = useCallback(async () => {
    setServerErrors([]);

    // Cijena ispod nabave je tvrda brana; backend bi je ionako odbio.
    if (isForbidden) {
      setStepCurrent(1);
      return;
    }

    // Koraci se smiju preskakati klikom na traku, pa se prije slanja vrte sve sheme redom.
    const failingStep = STEP_SCHEMAS.findIndex(
      (schema) => !validateYup(formValues as any, schema, setErrors)
    );

    if (failingStep >= 0) {
      setStepCurrent(failingStep);
      return;
    }

    setIsSaving(true);

    try {
      await savePricingOverrideAPI(formValues);
      onSuccess();
    } catch (error: any) {
      const list = error?.response?.data?.errors;

      if (Array.isArray(list)) {
        setServerErrors(list.map((e: any) => e.description));
      }
    } finally {
      setIsSaving(false);
    }
  }, [formValues, isForbidden, onSuccess]);

  /*----------------------------------------------------------------
      PRIKAZ
  ----------------------------------------------------------------*/

  const steps = [
    <WizardStep key="target" stepNumber={0} stepCurrent={stepCurrent}>
      <OverrideTargetStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        tenants={tenants}
        keyOptions={keyOptions}
      />
    </WizardStep>,
    <WizardStep key="amount" stepNumber={1} stepCurrent={stepCurrent}>
      <OverrideAmountStep
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        keyOption={keyOption}
      />
    </WizardStep>,
    <WizardStep key="terms" stepNumber={2} stepCurrent={stepCurrent}>
      <OverrideTermsStep formValues={formValues} setFormValues={setFormValues} errors={errors} />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="full"
      position="side"
      intro={{
        content: (
          <OverrideSummaryCard
            formValues={formValues}
            tenantName={tenantName}
            keyOption={keyOption}
            preview={preview}
          />
        ),
      }}
    >
      <ModalHeader
        title={override ? "Izmjena posebnog uvjeta" : "Novi poseban uvjet"}
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />

      {/* Traka stoji izvan tijela da ne odscrola sa sadržajem koraka. */}
      <div className="flex-shrink-0">
        <WizardStepBar
          stepLabels={OVERRIDE_STEP_LABELS}
          stepCurrent={stepCurrent}
          onStepClick={handleStepClick}
          firstStepCompleted={!!formValues.tenantId && !!formValues.keyCode}
          mandatoryCount={OVERRIDE_MANDATORY_STEP_COUNT}
        />
      </div>

      <ModalBody fixedFooterSpacing={false}>
        {serverErrors.length > 0 && (
          <div className="m-4 rounded-md border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-semibold text-red-700 mb-1">
              Poseban uvjet nije spremljen
            </p>
            <ul className="list-disc pl-5 text-sm text-red-700">
              {serverErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        <Wizard steps={steps} />
      </ModalBody>

      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isSaving}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default PricingOverrideEditCard;
